<template>
  <div class="stats-tab">
    <h3 class="gm-section-title">Base Stats ({{ totalStatsPoints }}/{{ maxStatsPoints }} points)</h3>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="loading-message">
      Loading stats...
    </div>

    <!-- Stats list using StatPresenter pattern -->
    <div v-else class="stats-container">
      <div class="stats-list">
        <div
          v-for="stat in templateStats"
          :key="stat.name"
          class="stat-presenter-wrapper"
        >
          <div class="gm-stat-presenter">
            <img
              class="stat-icon"
              :src="getStatIcon(stat.name)"
              :alt="stat.name"
              :title="stat.name"
            />
            <span class="stat-name">{{ stat.name }}</span>
            <div class="stat-controls">
              <button
                class="adjust-button decrement gm-btn gm-btn--sm"
                @click="decrementStat(stat.name)"
                :disabled="stat.value <= 1"
                title="Decrease stat"
              >
                -
              </button>
              <input
                type="number"
                class="stat-value-input"
                :value="stat.value"
                @input="updateStat(stat.name, $event)"
                @blur="validateStatValue(stat.name, $event)"
                min="1"
                :max="maxStatsPoints"
                title="Click to edit value directly"
              />
              <button
                class="adjust-button increment gm-btn gm-btn--sm"
                @click="incrementStat(stat.name)"
                :disabled="totalStatsPoints >= maxStatsPoints"
                title="Increase stat"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats summary and validation -->
    <div class="stats-info">
      <div class="points-display">
        <span :class="['points-used', { 'over-limit': totalStatsPoints > maxStatsPoints }]">
          Points Used: {{ totalStatsPoints }} / {{ maxStatsPoints }}
        </span>
      </div>
      <div v-if="totalStatsPoints > maxStatsPoints" class="warning">
        Warning: You have exceeded the maximum allowed stat points!
      </div>
      <div v-else-if="totalStatsPoints < maxStatsPoints" class="info">
        Remaining Points: {{ maxStatsPoints - totalStatsPoints }}
      </div>
    </div>

    <!-- Reset all stats option -->
    <div class="reset-section">
      <button class="reset-button gm-btn gm-btn--danger" @click="resetAllStats" title="Reset all stats to minimum values">
        Reset All Stats
      </button>
      <button class="distribute-button gm-btn gm-btn--primary" @click="distributeEvenly" title="Distribute points evenly across all stats">
        Distribute Evenly
      </button>
    </div>
  </div>
</template>

<script>
import StatsGameService from '@/services/statService.js';

export default {
  name: 'StatsTab',
  props: {
    template: {
      type: Object,
      required: true
    },
    service: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      statsService: StatsGameService,
      isLoading: false,
      availableStats: []
    };
  },
  computed: {
    totalStatsPoints() {
      if (!this.template) return 0;
      return this.template.data.stats.reduce((sum, stat) => sum + stat.value, 0);
    },
    maxStatsPoints() {
      if (!this.template) return 100;
      return this.template.validation.max_stats_points_count;
    },
    templateStats() {
      if (!this.template) return [];
      // Ensure all stats have proper values and are displayed
      return this.template.data.stats.map(stat => ({
        ...stat,
        value: Math.max(1, stat.value || 1) // Ensure minimum value of 1
      }));
    }
  },
  async created() {
    await this.initializeStats();
  },
  methods: {
    async initializeStats() {
      try {
        this.isLoading = true;
        await this.statsService.refreshStats();
        this.availableStats = this.statsService.listCachedStats();
        
        // Ensure all template stats have icons
        this.ensureAllStatsHaveValues();
      } catch (error) {
        console.error('Failed to initialize stats:', error);
      } finally {
        this.isLoading = false;
      }
    },

    ensureAllStatsHaveValues() {
      // Make sure all available stats are included in the template
      const currentStatNames = this.template.data.stats.map(stat => stat.name);
      
      this.availableStats.forEach(availableStat => {
        if (!currentStatNames.includes(availableStat.name)) {
          // Add missing stat with default value
          this.service.updateStat(availableStat.name, 1);
        }
      });
    },

    getStatIcon(statName) {
      const cachedIcon = this.statsService.getCachedStatImage(statName);
      if (cachedIcon) {
        return cachedIcon;
      }
      
      // Fallback to default icon or generate from stat name
      return this.getDefaultStatIcon(statName);
    },

    getDefaultStatIcon(statName) {
      // Fallback icon mapping for common stats
      const iconMap = {
        'strength': '/src/assets/images/stats/strength.webp',
        'dexterity': '/src/assets/images/stats/dexterity.webp',
        'constitution': '/src/assets/images/stats/constitution.webp',
        'intelligence': '/src/assets/images/stats/intelligence.webp',
        'wisdom': '/src/assets/images/stats/wisdom.webp',
        'charisma': '/src/assets/images/stats/charisma.webp',
        'agility': '/src/assets/images/stats/agility.webp',
        'endurance': '/src/assets/images/stats/endurance.webp',
        'perception': '/src/assets/images/stats/perception.webp',
        'luck': '/src/assets/images/stats/luck.webp'
      };

      const lowerStatName = statName.toLowerCase();
      return iconMap[lowerStatName] || '/src/assets/images/stats/default.webp';
    },

    updateStat(statName, event) {
      const value = parseInt(event.target.value, 10);
      if (!isNaN(value) && value >= 1) {
        this.service.updateStat(statName, Math.max(1, value));
        this.$emit('update');
      }
    },

    incrementStat(statName) {
      const currentStat = this.template.data.stats.find(stat => stat.name === statName);
      if (currentStat && this.totalStatsPoints < this.maxStatsPoints) {
        this.service.updateStat(statName, currentStat.value + 1);
        this.$emit('update');
      }
    },

    decrementStat(statName) {
      const currentStat = this.template.data.stats.find(stat => stat.name === statName);
      if (currentStat && currentStat.value > 1) {
        this.service.updateStat(statName, currentStat.value - 1);
        this.$emit('update');
      }
    },

    resetAllStats() {
      if (confirm('Are you sure you want to reset all stats to minimum values?')) {
        this.template.data.stats.forEach(stat => {
          this.service.updateStat(stat.name, 1);
        });
        this.$emit('update');
      }
    },

    distributeEvenly() {
      if (confirm('This will distribute available points evenly across all stats. Continue?')) {
        const statsCount = this.template.data.stats.length;
        const pointsPerStat = Math.floor(this.maxStatsPoints / statsCount);
        const remainingPoints = this.maxStatsPoints % statsCount;

        this.template.data.stats.forEach((stat, index) => {
          const value = pointsPerStat + (index < remainingPoints ? 1 : 0);
          this.service.updateStat(stat.name, Math.max(1, value));
        });
        this.$emit('update');
      }
    },

    validateStatValue(statName, event) {
      const value = parseInt(event.target.value, 10);
      if (isNaN(value) || value < 1) {
        // Reset to minimum value if invalid
        this.service.updateStat(statName, 1);
        this.$emit('update');
      } else if (value > this.maxStatsPoints) {
        // Cap at maximum if too high
        this.service.updateStat(statName, this.maxStatsPoints);
        this.$emit('update');
      }
    }
  }
};
</script>

<style scoped>
.stats-tab {
  padding: 20px 0;
}

/* Loading message */
.loading-message {
  text-align: center;
  color: var(--gm-text-dim);
  padding: 40px;
  font-style: italic;
}

/* Stats container */
.stats-container {
  margin-bottom: var(--gm-gap-lg);
}

.stats-list {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

/* GameMaster StatPresenter inspired styling */
.gm-stat-presenter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--gm-surface-1);
  padding: var(--gm-gap-sm) var(--gm-gap);
  border-radius: var(--gm-radius);
  border: 1px solid var(--gm-border-dim);
  gap: var(--gm-gap);
  transition: border-color 0.15s ease;
}

.gm-stat-presenter:hover {
  border-color: var(--gm-border);
}

/* Stat icon */
.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: contain;
  flex-shrink: 0;
  border: 1px solid var(--gm-border-dim);
}

/* Stat name */
.stat-name {
  flex: 1;
  color: var(--gm-text);
  font-weight: bold;
  font-size: 16px;
  text-transform: capitalize;
  min-width: 120px;
}

/* Stat controls (+/-) */
.stat-controls {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
  padding: 4px;
}

/* Editable stat value input */
.stat-value-input {
  font-size: 16px;
  font-weight: bold;
  color: var(--gm-text);
  min-width: 60px;
  width: 60px;
  text-align: center;
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
  padding: 6px 8px;
  border: 1px solid var(--gm-border-dim);
  transition: border-color 0.15s ease;
  outline: none;
  -moz-appearance: textfield;
}

/* Hide native number input spinner arrows in webkit browsers */
.stat-value-input::-webkit-outer-spin-button,
.stat-value-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.stat-value-input:focus {
  border-color: var(--gm-border);
  box-shadow: 0 0 0 2px var(--gm-accent-softer);
}

.stat-value-input:hover {
  border-color: var(--gm-border);
}

/* Stats info section */
.stats-info {
  background: var(--gm-surface-1);
  padding: var(--gm-gap);
  border-radius: var(--gm-radius);
  border: 1px solid var(--gm-border-dim);
  margin-bottom: var(--gm-gap);
}

.points-display {
  margin-bottom: var(--gm-gap-sm);
}

.points-used {
  font-weight: bold;
  color: var(--gm-success);
  font-size: 16px;
}

.points-used.over-limit {
  color: var(--gm-danger);
}

.warning {
  color: var(--gm-danger);
  font-weight: bold;
  background: rgba(201, 69, 63, 0.1);
  padding: var(--gm-gap-sm);
  border-radius: var(--gm-radius-sm);
  border-left: 3px solid var(--gm-danger);
  margin-top: var(--gm-gap-sm);
}

.info {
  color: var(--gm-success);
  font-weight: bold;
  background: rgba(143, 182, 60, 0.1);
  padding: var(--gm-gap-sm);
  border-radius: var(--gm-radius-sm);
  border-left: 3px solid var(--gm-success);
  margin-top: var(--gm-gap-sm);
}

/* Reset section */
.reset-section {
  display: flex;
  gap: var(--gm-gap-sm);
  justify-content: center;
  padding: var(--gm-gap) 0;
}

/* Header styling */
h3 {
  margin-top: 0;
  text-align: center;
  padding-bottom: var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

/* Responsive design */
@media (max-width: 768px) {
  .gm-stat-presenter {
    flex-direction: column;
    align-items: stretch;
    gap: var(--gm-gap-sm);
  }

  .stat-name {
    text-align: center;
    min-width: auto;
  }

  .stat-controls {
    justify-content: center;
  }

  .reset-section {
    flex-direction: column;
    align-items: center;
  }
}
</style>